import { db } from "./dbClient";

import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";

// KYC periodic refresh thresholds (F-004 / E-011)
const KYC_REFRESH_DAYS = { High: 180, Medium: 365, Low: 365 };

function isKYCRefreshOverdue(customer) {
  if (!customer?.lastScreened) return false;
  const days = KYC_REFRESH_DAYS[customer.riskLevel] || 365;
  const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return new Date(customer.lastScreened) < cutoff;
}

// ─── DILISENSE API ────────────────────────────────────────────────────────────
// All calls routed through server proxy — API key never touches the browser.
const PROXY_BASE = import.meta.env.VITE_PROXY_URL || "http://localhost:3001";

async function dilisenseCheck(name, type) {
  const endpoint = type === "Business" || type === "Entity" ? "entity" : "individual";
  const url = `${PROXY_BASE}/api/dilisense/${endpoint}?names=${encodeURIComponent(name)}&fuzzy_search=1`;
  const resp = await fetch(url, { method: "GET" });
  if (resp.status === 401) throw new Error("Dilisense API key not configured on server");
  if (resp.status === 400) throw new Error("Bad request — check the customer name");
  if (!resp.ok) throw new Error(`Dilisense API error: ${resp.status}`);
  return resp.json();
}

function parseResults(data) {
  const records = data.found_records || [];
  const sanctions = records.filter(r => r.source_type === "SANCTION");
  const pep       = records.filter(r => r.source_type === "PEP");
  const criminal  = records.filter(r => r.source_type === "CRIMINAL");
  return {
    totalHits: data.total_hits || 0,
    sanctions,
    pep,
    criminal,
    allRecords: records,
    sanctionsStatus: sanctions.length > 0 ? "failed" : "passed",
    watchlistStatus: pep.length > 0 ? "failed" : "passed",
    criminalStatus:  criminal.length > 0 ? "failed" : "passed",
  };
}

// ─── NAMSOR API ───────────────────────────────────────────────────────────────
// All calls routed through server proxy — API key never touches the browser.

const FATF_BLACKLIST = new Set(["IR", "KP", "MM"]);
const FATF_GREYLIST  = new Set([
  "AL","AO","BB","BF","BJ","BG","CM","CD","HR","GA",
  "GH","GY","HT","JM","KH","ML","MZ","NA","NI","NG",
  "PA","PK","PH","SN","ZA","SS","SY","TJ","TZ","TT",
  "TR","UG","AE","UZ","VN","YE",
]);

const COUNTRY_NAMES = {
  AF:"Afghanistan",AL:"Albania",DZ:"Algeria",AO:"Angola",AR:"Argentina",AU:"Australia",
  AZ:"Azerbaijan",BB:"Barbados",BY:"Belarus",BE:"Belgium",BZ:"Belize",BJ:"Benin",
  BO:"Bolivia",BA:"Bosnia",BW:"Botswana",BR:"Brazil",BG:"Bulgaria",BF:"Burkina Faso",
  KH:"Cambodia",CM:"Cameroon",CA:"Canada",TD:"Chad",CL:"Chile",CN:"China",CO:"Colombia",
  CG:"Congo",HR:"Croatia",CU:"Cuba",CD:"DRC",DK:"Denmark",EG:"Egypt",ET:"Ethiopia",
  FI:"Finland",FR:"France",GA:"Gabon",GM:"Gambia",GE:"Georgia",DE:"Germany",GH:"Ghana",
  GR:"Greece",GT:"Guatemala",GN:"Guinea",GY:"Guyana",HT:"Haiti",HN:"Honduras",HU:"Hungary",
  IN:"India",ID:"Indonesia",IR:"Iran",IQ:"Iraq",IE:"Ireland",IL:"Israel",IT:"Italy",
  JM:"Jamaica",JP:"Japan",JO:"Jordan",KZ:"Kazakhstan",KE:"Kenya",KP:"North Korea",
  KR:"South Korea",KW:"Kuwait",LB:"Lebanon",LY:"Libya",LT:"Lithuania",MK:"North Macedonia",
  MY:"Malaysia",ML:"Mali",MT:"Malta",MX:"Mexico",MD:"Moldova",MN:"Mongolia",MA:"Morocco",
  MZ:"Mozambique",MM:"Myanmar",NA:"Namibia",NP:"Nepal",NL:"Netherlands",NZ:"New Zealand",
  NI:"Nicaragua",NG:"Nigeria",NO:"Norway",OM:"Oman",PK:"Pakistan",PA:"Panama",PY:"Paraguay",
  PE:"Peru",PH:"Philippines",PL:"Poland",PT:"Portugal",QA:"Qatar",RO:"Romania",RU:"Russia",
  SA:"Saudi Arabia",SN:"Senegal",RS:"Serbia",SL:"Sierra Leone",ZA:"South Africa",
  SS:"South Sudan",ES:"Spain",LK:"Sri Lanka",SD:"Sudan",SY:"Syria",TJ:"Tajikistan",
  TZ:"Tanzania",TH:"Thailand",TT:"Trinidad & Tobago",TN:"Tunisia",TR:"Turkey",
  TM:"Turkmenistan",UG:"Uganda",UA:"Ukraine",AE:"UAE",GB:"United Kingdom",US:"United States",
  UY:"Uruguay",UZ:"Uzbekistan",VE:"Venezuela",VN:"Vietnam",YE:"Yemen",ZM:"Zambia",ZW:"Zimbabwe",
};

function parseName(fullName) {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

// No apiKey param — proxy reads it from .env.server
async function namsorCheck(fullName) {
  const { firstName, lastName } = parseName(fullName);
  const proxyUrl = `${PROXY_BASE}/api/namsor`;
  const resp = await fetch(proxyUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({ firstName, lastName: lastName || "-" }),
  });
  if (resp.status === 500) throw new Error("NamSor API key not configured on server");
  if (!resp.ok) throw new Error(`NamSor error: ${resp.status}`);
  const data = await resp.json();
  const origin = data.countryOrigin || null;
  return {
    countryOrigin:    origin,
    countryOriginAlt: data.countryOriginAlt || null,
    score:            data.score || 0,
    isFATFBlacklist:  FATF_BLACKLIST.has(origin),
    isFATFHighRisk:   FATF_GREYLIST.has(origin) || FATF_BLACKLIST.has(origin),
  };
}

// ─── AI RISK SYNTHESIS ────────────────────────────────────────────────────────
async function aiRiskSynthesis(customerName, sanctionsData, nameOrigin) {
  const proxyUrl = `${PROXY_BASE}/api/ai`;
  const originName = nameOrigin.countryOrigin || "Unknown";
  const fatfStatus = nameOrigin.isFATFBlacklist
    ? "FATF Blacklist (highest risk)"
    : nameOrigin.isFATFHighRisk
    ? "FATF Grey List (increased monitoring)"
    : "Not on FATF lists";

  const sanctionHits = (sanctionsData?.sanctions || []).map(m => m.name || m.entity_name).join(", ") || "None";
  const pepHits      = (sanctionsData?.watchlist || sanctionsData?.pep || []).map(m => m.name || m.entity_name).join(", ") || "None";
  const criminalHits = (sanctionsData?.criminal  || []).map(m => m.name || m.entity_name).join(", ") || "None";
  const totalHits    = (sanctionsData?.sanctions?.length || 0)
                     + ((sanctionsData?.watchlist || sanctionsData?.pep)?.length || 0)
                     + (sanctionsData?.criminal?.length || 0);

  const prompt = `You are a senior AML compliance analyst. Synthesize the following screening results for subject "${customerName}" and provide a concise risk assessment.\n\nSANCTIONS SCREENING:\n- Sanctions list hits: ${sanctionHits}\n- PEP/Watchlist hits: ${pepHits}\n- Criminal record hits: ${criminalHits}\n- Total hits: ${totalHits}\n\nNAME INTELLIGENCE (NamSor):\n- Predicted country of name origin: ${originName} (${nameOrigin.countryOrigin || "?"})\n- Confidence: ${Math.round((nameOrigin.score || 0) * 100)}%\n- FATF status of origin country: ${fatfStatus}\n\nProvide a 2–3 sentence risk assessment that considers whether the combination of screening results and name origin is suspicious, what the AML risk level is, and what action (if any) is recommended. Be specific about why the combination matters — do not just restate the facts.`;

  const resp = await fetch(proxyUrl, {
    method: "POST",
    signal: AbortSignal.timeout(60000),
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({
      system: "You are a senior AML/KYC compliance analyst. Be concise, direct, and actionable.",
      messages: [{ role: "user", content: prompt }],
      max_tokens: 200,
    }),
  });
  if (!resp.ok) throw new Error("AI synthesis failed");
  const data = await resp.json();
  return data.content?.[0]?.text || data.choices?.[0]?.message?.content || "";
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", disabled = false, small = false, style: extraStyle = {} }) {
  const C = useTheme();
  const base = {
    primary: { background: C.accent,   color: C.bg,      border: "none" },
    ghost:   { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` },
    danger:  { background: "rgba(255,68,68,0.1)",   color: C.danger,  border: `1px solid ${C.danger}` },
    success: { background: "rgba(0,230,118,0.1)",   color: C.success, border: `1px solid ${C.success}` },
    subtle:  { background: C.surfaceAlt, color: C.textMid, border: `1px solid ${C.border}` },
    warn:    { background: "rgba(255,140,0,0.1)",   color: C.warn,    border: `1px solid ${C.warn}` },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...base, padding: small ? "5px 12px" : "9px 18px", borderRadius: 7,
        cursor: disabled ? "not-allowed" : "pointer", fontFamily: "'DM Mono', monospace",
        fontWeight: 600, fontSize: small ? 11 : 13, opacity: disabled ? 0.45 : 1,
        transition: "opacity 0.15s, transform 0.1s", letterSpacing: "0.03em",
        display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", ...extraStyle }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
    >{children}</button>
  );
}

function Badge({ text, type = "default" }) {
  const C = useTheme();
  const styles = {
    pending:  { bg: "rgba(255,140,0,0.1)",   color: C.warn,    border: `1px solid rgba(255,140,0,0.35)` },
    passed:   { bg: "rgba(0,230,118,0.1)",   color: C.success, border: `1px solid rgba(0,230,118,0.35)` },
    failed:   { bg: "rgba(255,68,68,0.1)",   color: C.danger,  border: `1px solid rgba(255,68,68,0.35)` },
    default:  { bg: "rgba(138,155,181,0.1)", color: C.textMid, border: `1px solid ${C.border}` },
    scanning: { bg: "rgba(0,212,255,0.08)",  color: C.accent,  border: `1px solid rgba(0,212,255,0.3)` },
  }[type] || { bg: "rgba(138,155,181,0.1)", color: C.textMid, border: `1px solid ${C.border}` };
  return (
    <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, background: styles.bg,
      color: styles.color, border: styles.border, fontFamily: "'DM Mono', monospace",
      fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {type === "scanning" && <span style={{ display: "inline-block", width: 6, height: 6,
        borderRadius: "50%", background: C.accent, animation: "kycPulse 1s infinite" }} />}
      {text}
    </span>
  );
}

function RiskBadge({ level }) {
  const C = useTheme();
  const map = {
    Low:    { color: C.success, bg: "rgba(0,230,118,0.1)",  border: "rgba(0,230,118,0.3)" },
    Medium: { color: C.warn,    bg: "rgba(255,140,0,0.1)",  border: "rgba(255,140,0,0.3)" },
    High:   { color: C.danger,  bg: "rgba(255,68,68,0.1)",  border: "rgba(255,68,68,0.3)" },
  };
  const s = map[level] || map.Medium;
  return (
    <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, background: s.bg,
      color: s.color, border: `1px solid ${s.border}`, fontFamily: "'DM Mono', monospace",
      fontWeight: 700 }}>{level}</span>
  );
}

// ─── MATCH DETAIL CARD ─────────────────────────────────────────────────────────
function MatchCard({ record }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const typeColors = {
    SANCTION: { color: C.danger, bg: "rgba(255,68,68,0.1)",  label: "⚠ SANCTION" },
    PEP:      { color: C.warn,   bg: "rgba(255,140,0,0.1)",  label: "★ PEP" },
    CRIMINAL: { color: "#FF6B6B",bg: "rgba(255,68,68,0.15)", label: "✕ CRIMINAL" },
  }[record.source_type] || { color: C.textMid, bg: C.surfaceAlt, label: record.source_type };

  return (
    <div style={{ background: C.bg, border: `1px solid ${record.source_type === "SANCTION" ? "rgba(255,68,68,0.35)" : record.source_type === "PEP" ? "rgba(255,140,0,0.35)" : C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: typeColors.bg, color: typeColors.color, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>{typeColors.label}</span>
            {record.pep_type && <span style={{ fontSize: 10, color: C.textDim }}>{record.pep_type.replace(/_/g, " ")}</span>}
          </div>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{record.name}</div>
          {record.alias_names?.length > 0 && (
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{t('kyc.match.alsoKnownAs')} {record.alias_names.slice(0, 3).join(", ")}</div>
          )}
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{t('kyc.match.source')} <span style={{ color: C.textMid }}>{record.source_id}</span></div>
        </div>
        <button onClick={() => setExpanded(v => !v)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textMid, padding: "3px 8px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
          {expanded ? t('common.less') : t('common.details')}
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
          {record.date_of_birth?.length > 0 && <div><span style={{ color: C.textDim }}>DOB: </span><span style={{ color: C.text }}>{record.date_of_birth.join(", ")}</span></div>}
          {record.citizenship?.length > 0 && <div><span style={{ color: C.textDim }}>Citizenship: </span><span style={{ color: C.text }}>{record.citizenship.join(", ")}</span></div>}
          {record.place_of_birth?.length > 0 && <div><span style={{ color: C.textDim }}>Place of birth: </span><span style={{ color: C.text }}>{record.place_of_birth.join(", ")}</span></div>}
          {record.positions?.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ color: C.textDim, marginBottom: 4 }}>Positions:</div>
              {record.positions.slice(0, 4).map((p, i) => <div key={i} style={{ color: C.textMid, fontSize: 11, paddingLeft: 8, borderLeft: `2px solid ${C.border}`, marginBottom: 2 }}>{p}</div>)}
            </div>
          )}
          {record.sanction_details?.length > 0 && (
            <div style={{ gridColumn: "1 / -1", background: "rgba(255,68,68,0.07)", padding: "8px 12px", borderRadius: 6, border: `1px solid rgba(255,68,68,0.2)` }}>
              <div style={{ color: C.danger, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Sanction Details:</div>
              {record.sanction_details.map((d, i) => <div key={i} style={{ color: C.textMid, fontSize: 11 }}>{d}</div>)}
            </div>
          )}
          {record.description?.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ color: C.textDim, fontSize: 11 }}>{record.description.join(" · ")}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CUSTOMER DRAWER ──────────────────────────────────────────────────────────
// REMOVED: apiKey and namsorApiKey props — keys are now server-side only
function CustomerDrawer({ customer, onClose, onSave, onRunScreening, onRunNameOrigin, scanningId, checkingNameOriginId }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...customer });
  const scanning = scanningId === customer.id;
  const allPassed = customer.sanctions === "passed" && customer.watchlist === "passed" && customer.criminal === "passed";
  const anyFailed = customer.sanctions === "failed" || customer.watchlist === "failed" || customer.criminal === "failed";

  const checks = [
    { key: "sanctions", statusKey: "sanctions", label: t('kyc.checks.sanctions'),  desc: t('kyc.checks.sanctionsDesc') },
    { key: "watchlist", statusKey: "watchlist", label: t('kyc.checks.watchlist'),  desc: t('kyc.checks.watchlistDesc') },
    { key: "criminal",  statusKey: "criminal",  label: t('kyc.checks.criminal'),   desc: t('kyc.checks.criminalDesc') },
  ];

  const matchData = customer.matchData || {};

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "stretch", justifyContent: "flex-end" }} onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", width: 520, background: C.surface, borderLeft: `1px solid ${C.border}`, display: "flex", flexDirection: "column", overflowY: "auto", zIndex: 1 }}>

        {/* Header */}
        <div style={{ padding: "24px 28px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.12em", marginBottom: 4 }}>{t('kyc.customerProfile')}</div>
            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 700 }}>{customer.name}</div>
            <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
              <RiskBadge level={customer.riskLevel} />
              {anyFailed  && <Badge text="⚠ MATCH FOUND" type="failed" />}
              {allPassed && customer.sanctions !== "pending" && <Badge text="✓ CLEAR" type="passed" />}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 22 }}>✕</button>
            <button onClick={() => {
              const checkRows = checks.map(c => `<tr><td>${c.label}</td><td style="font-weight:600;color:${customer[c.statusKey]==="passed"?"#276749":customer[c.statusKey]==="failed"?"#C53030":"#744210"}">${(customer[c.statusKey]||"pending").toUpperCase()}</td><td>${c.desc}</td></tr>`).join("");
              const matchRows = Object.entries(matchData).flatMap(([type, items]) => (items||[]).map(m => `<tr><td>${type}</td><td>${m.name||"—"}</td><td>${m.country||"—"}</td><td>${m.score!=null?m.score+"%":"—"}</td></tr>`)).join("");
              const w = window.open("","_blank");
              if(!w || !w.document) return;
              w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>KYC — ${customer.name}</title><style>
              @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
              *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm;font-size:11px}
              .header{display:flex;align-items:center;gap:10px;margin-bottom:6mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
              .logo-hex{width:26px;height:26px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);flex-shrink:0}
              .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:16px}
              .doc-title{font-family:'Syne',sans-serif;font-size:18px;font-weight:700;margin-bottom:1mm}
              .doc-meta{font-size:9px;color:#4A5568;margin-bottom:5mm}
              .section{margin-bottom:5mm}.section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:11px;letter-spacing:0.1em;margin-bottom:2mm;padding-bottom:1mm;border-bottom:1px solid #E2E8F0}
              table{width:100%;border-collapse:collapse;font-size:10px}th{background:#0A2342;color:#fff;padding:5px 7px;text-align:left;font-size:9px}
              td{padding:4px 7px;border-bottom:1px solid #E2E8F0;vertical-align:top}tr:nth-child(even) td{background:#F7FAFC}
              .footer{margin-top:6mm;padding-top:2mm;border-top:1px solid #E2E8F0;font-size:8px;color:#8A9BB5;display:flex;justify-content:space-between}
              @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
              </style></head><body>
              <div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
              <div class="doc-title">Sanctions Screening — ${customer.name}</div>
              <div class="doc-meta">Generated: ${new Date().toLocaleString()} · Risk: ${customer.riskLevel||"—"} · Type: ${customer.type||"—"}</div>
              <div class="section"><div class="section-title">CUSTOMER DETAILS</div><table><tbody>
                <tr><td><b>Name</b></td><td>${customer.name||"—"}</td><td><b>Type</b></td><td>${customer.type||"—"}</td></tr>
                <tr><td><b>Country</b></td><td>${customer.country||"—"}</td><td><b>Risk</b></td><td>${customer.riskLevel||"—"}</td></tr>
                <tr><td><b>Date Added</b></td><td>${customer.dateAdded||"—"}</td><td><b>Hits</b></td><td>${customer.totalHits||0}</td></tr>
              </tbody></table></div>
              <div class="section"><div class="section-title">SCREENING RESULTS</div><table><thead><tr><th>Check</th><th>Status</th><th>Description</th></tr></thead><tbody>${checkRows}</tbody></table></div>
              ${matchRows?`<div class="section"><div class="section-title">MATCHES</div><table><thead><tr><th>Type</th><th>Name</th><th>Country</th><th>Score</th></tr></thead><tbody>${matchRows}</tbody></table></div>`:""}
              <div class="footer"><span>CrossRegGuard — Confidential</span><span>KYC ${customer.id||""} · ${new Date().toLocaleDateString()}</span></div>
              </body></html>`);
              w.document.close(); setTimeout(()=>w.print(),800);
            }} style={{ background:"transparent", border:"1px solid #00D4FF", borderRadius:6, color:"#00D4FF", padding:"4px 11px", cursor:"pointer", fontSize:11, fontFamily:"'DM Mono',monospace" }}>⬇ PDF</button>
          </div>
        </div>

        <div style={{ padding: "24px 28px", flex: 1, display: "flex", flexDirection: "column", gap: 22 }}>

          {/* Editable fields */}
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 12, fontWeight: 600 }}>{t('kyc.customerDetails')}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: t('kyc.fields.fullName'), key: "name", full: true },
                { label: t('kyc.fields.type'), key: "type", type: "select", options: [t('kyc.fields.individual'), t('kyc.fields.business'), t('kyc.fields.entity')] },
                { label: t('kyc.fields.country'), key: "country" },
                { label: t('kyc.fields.riskLevel'), key: "riskLevel", type: "select", options: [t('kyc.riskLevels.low'), t('kyc.riskLevels.medium'), t('kyc.riskLevels.high')] },
                { label: t('kyc.fields.dateAdded'), key: "dateAdded", disabled: true },
              ].map(field => (
                <div key={field.key} style={{ gridColumn: field.full ? "1 / -1" : undefined }}>
                  <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{field.label.toUpperCase()}</div>
                  {field.type === "select" ? (
                    <select value={form[field.key] || ""} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                      {field.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={form[field.key] || ""} disabled={field.disabled} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                      style={{ width: "100%", background: field.disabled ? "transparent" : C.bg, border: `1px solid ${field.disabled ? "transparent" : C.border}`, borderRadius: 6, color: field.disabled ? C.textDim : C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('kyc.fields.notes').toUpperCase()}</div>
              <textarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginTop: 12 }}>
              <Btn onClick={() => onSave(form)} style={{ flex: 1 }}>{t('kyc.saveChanges')}</Btn>
            </div>
          </div>

          {/* Screening Results */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600 }}>{t('kyc.complianceScreening')}</div>
              {/* REMOVED: disabled={!apiKey} — screening always available via proxy */}
              <Btn small variant={scanning ? "subtle" : "primary"} disabled={scanning} onClick={() => onRunScreening(customer.id)}>
                {scanning ? t('common.screening') : t('common.runChecks')}
              </Btn>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {checks.map(check => {
                const val = customer[check.statusKey];
                const badgeType = scanning ? "scanning" : (val === "passed" ? "passed" : val === "failed" ? "failed" : "pending");
                const matchRecords = matchData[check.key] || [];
                return (
                  <div key={check.key} style={{ background: C.bg, border: `1px solid ${val === "failed" ? "rgba(255,68,68,0.3)" : C.border}`, borderRadius: 8, padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{check.label}</div>
                        <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{check.desc}</div>
                        {matchRecords.length > 0 && !scanning && (
                          <div style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{t('kyc.drawer.matchCount', { count: matchRecords.length })}</div>
                        )}
                      </div>
                      <Badge text={scanning ? t('kyc.drawer.scanning') : val} type={badgeType} />
                    </div>
                    {matchRecords.length > 0 && !scanning && (
                      <div style={{ marginTop: 10 }}>
                        {matchRecords.map((r, i) => <MatchCard key={i} record={r} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {customer.lastScreened && (
              <div style={{ color: C.textDim, fontSize: 11, marginBottom: 10 }}>
                {t('kyc.drawer.lastScreened', { date: new Date(customer.lastScreened).toLocaleString() })}
                {customer.totalHits > 0 && <span style={{ color: C.danger, marginLeft: 8 }}>· {t('kyc.drawer.totalHits', { count: customer.totalHits })}</span>}
              </div>
            )}

            {anyFailed && !scanning && (
              <div style={{ padding: "14px 16px", background: "rgba(255,68,68,0.07)", border: `1px solid rgba(255,68,68,0.25)`, borderRadius: 8 }}>
                <div style={{ color: C.danger, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t('kyc.drawer.matchFoundTitle')}</div>
                <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>{t('kyc.drawer.matchFoundBody')}</div>
              </div>
            )}
            {allPassed && !scanning && customer.sanctions !== "pending" && (
              <div style={{ padding: "14px 16px", background: "rgba(0,230,118,0.07)", border: `1px solid rgba(0,230,118,0.25)`, borderRadius: 8 }}>
                <div style={{ color: C.success, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t('kyc.drawer.allClearTitle')}</div>
                <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>{t('kyc.drawer.allClearBody')}</div>
              </div>
            )}
          </div>

          {/* ── Name Intelligence — always visible, key is server-side ───────── */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600 }}>{t('kyc.nameIntelligence')}</div>
              {!customer.nameOrigin && (
                <Btn small variant="subtle"
                  disabled={checkingNameOriginId === customer.id}
                  onClick={() => onRunNameOrigin(customer.id)}>
                  {checkingNameOriginId === customer.id ? t('kyc.drawer.checkingOrigin') : t('kyc.drawer.checkNameOrigin')}
                </Btn>
              )}
              {customer.nameOrigin && (
                <Btn small variant="ghost" onClick={() => onRunNameOrigin(customer.id)}
                  disabled={checkingNameOriginId === customer.id}>
                  {checkingNameOriginId === customer.id ? t('kyc.drawer.checkingOrigin') : t('kyc.drawer.reRun')}
                </Btn>
              )}
            </div>

            {!customer.nameOrigin && checkingNameOriginId !== customer.id && (
              <div style={{ padding: "14px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textDim, fontSize: 12 }}>
                {t('kyc.drawer.checkNameOriginHint')}
              </div>
            )}

            {customer.nameOrigin && (() => {
              const { countryOrigin, countryOriginAlt, score, isFATFBlacklist, isFATFHighRisk, aiAssessment } = customer.nameOrigin;
              const originName  = COUNTRY_NAMES[countryOrigin] || countryOrigin || "Unknown";
              const altName     = COUNTRY_NAMES[countryOriginAlt] || countryOriginAlt;
              const borderColor = isFATFBlacklist ? "rgba(255,68,68,0.35)" : isFATFHighRisk ? "rgba(255,140,0,0.35)" : "rgba(0,212,255,0.2)";
              const bgColor     = isFATFBlacklist ? "rgba(255,68,68,0.07)" : isFATFHighRisk ? "rgba(255,140,0,0.07)" : "rgba(0,212,255,0.04)";
              const statusColor = isFATFBlacklist ? C.danger : isFATFHighRisk ? C.warn : C.accent;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: (isFATFBlacklist || isFATFHighRisk) ? 10 : 0 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{t('kyc.drawer.nameOrigin', { name: originName })}</div>
                        <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>
                          {altName && altName !== originName && t('kyc.drawer.nameAlt', { name: altName })}
                          {t('kyc.drawer.confidence', { pct: Math.round((score || 0) * 100) })}
                        </div>
                      </div>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 700, color: statusColor, letterSpacing: "0.05em" }}>
                        {countryOrigin || "—"}
                      </span>
                    </div>
                    {isFATFBlacklist && (
                      <div style={{ background: "rgba(255,68,68,0.12)", border: `1px solid rgba(255,68,68,0.3)`, borderRadius: 6, padding: "8px 12px" }}>
                        <div style={{ color: C.danger, fontSize: 12, fontWeight: 700 }}>⚠ FATF Blacklist Country</div>
                        <div style={{ color: C.textMid, fontSize: 11, marginTop: 2 }}>This name originates from a jurisdiction subject to FATF countermeasures (Iran, North Korea, Myanmar). Highest risk — enhanced due diligence required.</div>
                      </div>
                    )}
                    {!isFATFBlacklist && isFATFHighRisk && (
                      <div style={{ background: "rgba(255,140,0,0.1)", border: `1px solid rgba(255,140,0,0.3)`, borderRadius: 6, padding: "8px 12px" }}>
                        <div style={{ color: C.warn, fontSize: 12, fontWeight: 700 }}>◉ FATF Grey List Country</div>
                        <div style={{ color: C.textMid, fontSize: 11, marginTop: 2 }}>This name originates from a jurisdiction under FATF increased monitoring. Enhanced due diligence recommended.</div>
                      </div>
                    )}
                  </div>

                  {/* AI Risk Assessment block */}
                  {aiAssessment && (
                    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
                      <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>AI RISK SYNTHESIS</div>
                      <div style={{ color: C.text, fontSize: 12, lineHeight: 1.7 }}>{aiAssessment}</div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SETTINGS PANEL ───────────────────────────────────────────────────────────
// REMOVED: APIKeyPanel entirely — keys live in .env.server on the proxy only.
// Settings now only shows KYC refresh thresholds and notification preferences.
function SettingsPanel({ C }) {
  const { t } = useTranslation();
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24, maxWidth: 560 }}>
      <div style={{ fontFamily: "'Syne', sans-serif", color: C.text, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
        {t('kyc.settings.title')}
      </div>
      <div style={{ color: C.textDim, fontSize: 12, marginBottom: 20 }}>
        {t('kyc.settings.subtitle')}
      </div>

      {/* API Status — read-only indicators (no input fields) */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12 }}>
          API SERVICES (CONFIGURED ON SERVER)
        </div>
        {[
          { label: "Dilisense",   desc: "Sanctions, PEP & Criminal screening — OFAC SDN, UN, EU, HMT and more" },
          { label: "NamSor",      desc: "Name origin and FATF jurisdiction intelligence" },
        ].map(svc => (
          <div key={svc.label} style={{ display: "flex", alignItems: "flex-start", gap: 12,
            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "12px 16px", marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.success,
              flexShrink: 0, marginTop: 4, boxShadow: `0 0 6px ${C.success}` }} />
            <div>
              <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{svc.label}</div>
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{svc.desc}</div>
              <div style={{ color: C.success, fontSize: 10, marginTop: 4, fontFamily: "'DM Mono', monospace" }}>
                ✓ Key loaded from server environment
              </div>
            </div>
          </div>
        ))}
        <div style={{ color: C.textDim, fontSize: 11, marginTop: 10, padding: "10px 14px",
          background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7 }}>
          🔒 API keys are stored securely in the server <code style={{ fontFamily: "'DM Mono', monospace",
            background: C.bg, padding: "1px 5px", borderRadius: 3 }}>.env.server</code> file
          and are never exposed to the browser.
        </div>
      </div>

      {/* KYC Refresh Schedule */}
      <div>
        <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12 }}>
          KYC REFRESH SCHEDULE
        </div>
        {[
          { risk: "High",   days: 180, color: C.danger },
          { risk: "Medium", days: 365, color: C.warn },
          { risk: "Low",    days: 365, color: C.success },
        ].map(row => (
          <div key={row.risk} style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "10px 16px", marginBottom: 6,
            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <RiskBadge level={row.risk} />
              <span style={{ color: C.textMid, fontSize: 12 }}>Risk customers</span>
            </div>
            <span style={{ color: row.color, fontFamily: "'DM Mono', monospace",
              fontSize: 12, fontWeight: 700 }}>
              Every {row.days} days
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CSV IMPORT ───────────────────────────────────────────────────────────────
function processCSV(text) {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
    const row = {};
    headers.forEach((h, i) => { row[h] = vals[i] || ""; });
    const riskRaw = (row.risk_level || row.risk || "").trim();
    const riskCapitalised = riskRaw.charAt(0).toUpperCase() + riskRaw.slice(1).toLowerCase();
    const riskLevel = ["Low", "Medium", "High"].includes(riskCapitalised) ? riskCapitalised : "Medium";
    return {
      id: `import-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name:       row.name || row.full_name || row.customer_name || "Unknown",
      type:       row.type || row.entity_type || "Individual",
      country:    row.country || row.nationality || "",
      riskLevel,
      notes:      row.notes || row.comments || "",
      dateAdded:  row.date_added || new Date().toISOString().split("T")[0],
      sanctions:  "pending",
      watchlist:  "pending",
      criminal:   "pending",
      totalHits:  0,
      matchData:  {},
      lastScreened: null,
      nameOrigin: null,
    };
  });
}

// ─── MAIN PAGE COMPONENT ──────────────────────────────────────────────────────
// REMOVED: all apiKey / namsorApiKey state, localStorage reads, and Settings key UI.
export default function SanctionsScreeningPage({ currentUser, company }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);

  const [customers,             setCustomers]             = useState([]);
  const [selectedCustomer,      setSelectedCustomer]      = useState(null);
  const [scanningId,            setScanningId]            = useState(null);
  const [checkingNameOriginId,  setCheckingNameOriginId]  = useState(null);
  const [bulkScanning,          setBulkScanning]          = useState(false);
  const [activeTab,             setActiveTab]             = useState("customers"); // "customers" | "settings"
  const [searchQuery,           setSearchQuery]           = useState("");
  const [riskFilter,            setRiskFilter]            = useState("All");
  const [statusFilter,          setStatusFilter]          = useState("All");
  const [showCSVModal,          setShowCSVModal]          = useState(false);
  const [csvText,               setCsvText]               = useState("");
  const [csvPreview,            setCsvPreview]            = useState([]);
  const [notification,          setNotification]          = useState(null);
  const fileInputRef = useRef(null);

  // ── Load from Neon ────────────────────────────────────────────────────────
  useEffect(() => { if (company?.id) loadCustomers(); }, [company?.id]);

  async function loadCustomers() {
    // Load from watchlist table (primary source for watchlist data)
    const { data: watchlistData } = await db.from("watchlist")
      .select("*")
      .eq("company_id", company.id)
      .order("created_at", { ascending: false }).execute();

    // Also load from approved KYC customers so users can add them to watchlist
    const { data: kycData } = await db.from("kyc_submissions")
      .select("id, first_name, last_name, business_name, country, type")
      .eq("company_id", company.id)
      .eq("status", "approved")
      .order("submitted_at", { ascending: false }).execute();

    const watchlistCustomers = (watchlistData || []).map(row => ({
      id:           row.id,
      name:         row.customer_name,
      type:         row.entity_type   || "Individual",
      country:      row.country       || "",
      riskLevel:    row.risk_level    || "Medium",
      notes:        row.notes         || "",
      dateAdded:    row.created_at?.split("T")[0] || "",
      sanctions:    row.sanctions_status  || "pending",
      watchlist:    row.watchlist_status  || "pending",
      criminal:     row.criminal_status   || "pending",
      totalHits:    row.total_hits        || 0,
      matchData:    row.match_data        || {},
      lastScreened: row.last_screened     || null,
      nameOrigin:   row.name_origin       || null,
    }));

    // Add KYC customers not already in watchlist
    const existingNames = new Set(watchlistCustomers.map(c => (c.name || "").toLowerCase()));
    const kycCustomers = (kycData || []).map(c => {
      const displayName = c.business_name || [c.first_name, c.last_name].filter(Boolean).join(" ");
      return {
        id:           `kyc_${c.id}`,
        name:         displayName,
        type:         c.type === "business" ? "Business" : "Individual",
        country:      c.country || "",
        riskLevel:    "Medium",
        notes:        "",
        dateAdded:    new Date().toISOString().split("T")[0],
        sanctions:    "pending",
        watchlist:    "pending",
        criminal:     "pending",
        totalHits:    0,
        matchData:    {},
        lastScreened: null,
        nameOrigin:   null,
      };
    }).filter(c => !existingNames.has((c.name || "").toLowerCase()));

    // Combine both sources, with watchlist items first
    setCustomers([...watchlistCustomers, ...kycCustomers]);
  }

  function notify(message, type = "success") {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  }

  // ── Add customer ────────────────────────────────────────────────────────────
  async function addCustomer(data) {
    const mockId = `mock-${Date.now()}`;
    const newC = {
      id:           mockId,
      name:         data.name,
      type:         data.type         || "Individual",
      country:      data.country      || "",
      riskLevel:    data.riskLevel    || "Medium",
      notes:        data.notes        || "",
      dateAdded:    new Date().toISOString().split("T")[0],
      sanctions:    "pending",
      watchlist:    "pending",
      criminal:     "pending",
      totalHits:    0,
      matchData:    {},
      lastScreened: null,
      nameOrigin:   null,
    };

    if (company?.id) {
      const { data: inserted, error } = await db.from("watchlist")
        .insert({
          company_id:        company.id,
          customer_name:     newC.name,
          entity_type:       newC.type,
          country:           newC.country,
          risk_level:        newC.riskLevel,
          notes:             newC.notes,
          sanctions_status:  "pending",
          watchlist_status:  "pending",
          criminal_status:   "pending",
          total_hits:        0,
          match_data:        {},
        })
        .execute();
      if (!error && inserted && Array.isArray(inserted) && inserted.length > 0) newC.id = inserted[0].id;
    }

    setCustomers(prev => [newC, ...prev]);
    await log({ action: "customer_added", module: "sanctions", entityName: newC.name,
      newValue: { type: newC.type, riskLevel: newC.riskLevel } });
    notify(`${newC.name} added successfully.`);
    return newC;
  }

  // ── Save changes ────────────────────────────────────────────────────────────
  async function saveCustomer(updated) {
    setCustomers(prev => prev.map(c => c.id === updated.id ? updated : c));
    const idStr = String(updated.id ?? "");
    if (company?.id && !idStr.startsWith("mock-")) {
      await db.from("watchlist").update({
        customer_name:  updated.name,
        entity_type:    updated.type,
        country:        updated.country,
        risk_level:     updated.riskLevel,
        notes:          updated.notes,
      }).eq("id", updated.id).execute();
    }
    setSelectedCustomer(updated);
    notify(t('kyc.notifications.saved'));
  }

  // ── Run sanctions screening (Dilisense via proxy) ──────────────────────────
  async function runScreening(customerId) {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    setScanningId(customerId);

    let updated = { ...customer };
    try {
      const data = await dilisenseCheck(customer.name, customer.type);
      const results = parseResults(data);
      updated = {
        ...customer,
        sanctions:    results.sanctionsStatus,
        watchlist:    results.watchlistStatus,
        criminal:     results.criminalStatus,
        totalHits:    results.totalHits,
        matchData:    { sanctions: results.sanctions, watchlist: results.pep, criminal: results.criminal },
        lastScreened: new Date().toISOString(),
      };
    } catch (err) {
      notify(`Screening failed: ${err.message}`, "error");
      setScanningId(null);
      return;
    }

    setCustomers(prev => prev.map(c => c.id === customerId ? updated : c));
    if (selectedCustomer?.id === customerId) setSelectedCustomer(updated);

    const idStr = String(updated.id ?? "");
    if (company?.id && !idStr.startsWith("mock-")) {
      await db.from("watchlist").update({
        sanctions_status: updated.sanctions,
        watchlist_status: updated.watchlist,
        criminal_status:  updated.criminal,
        total_hits:       updated.totalHits,
        match_data:       updated.matchData,
        last_screened:    updated.lastScreened,
      }).eq("id", updated.id).execute();
    }

    await log({
      action:     "sanctions_screening_run",
      module:     "sanctions",
      entityName: customer.name,
      newValue:   { sanctions: updated.sanctions, watchlist: updated.watchlist, criminal: updated.criminal, totalHits: updated.totalHits },
    });

    notify(updated.totalHits > 0
      ? `⚠ ${updated.totalHits} match${updated.totalHits !== 1 ? "es" : ""} found for ${customer.name}`
      : `✓ ${customer.name} — no matches found`);
    setScanningId(null);
  }

  // ── Run NamSor name origin check (via proxy) ───────────────────────────────
  async function runNameOrigin(customerId) {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    setCheckingNameOriginId(customerId);

    let nameOrigin = null;
    let aiAssessment = "";
    try {
      nameOrigin = await namsorCheck(customer.name);
      try {
        aiAssessment = await aiRiskSynthesis(customer.name, customer.matchData || {}, nameOrigin);
      } catch {
        aiAssessment = "";
      }
      nameOrigin = { ...nameOrigin, aiAssessment };
    } catch (err) {
      notify(`Name origin check failed: ${err.message}`, "error");
      setCheckingNameOriginId(null);
      return;
    }

    const updated = { ...customer, nameOrigin };
    setCustomers(prev => prev.map(c => c.id === customerId ? updated : c));
    if (selectedCustomer?.id === customerId) setSelectedCustomer(updated);

    const idStr = String(updated.id ?? "");
    if (company?.id && !idStr.startsWith("mock-")) {
      await db.from("watchlist").update({ name_origin: nameOrigin }).eq("id", updated.id).execute();
    }

    await log({
      action:     "name_origin_check_run",
      module:     "sanctions",
      entityName: customer.name,
      newValue:   { countryOrigin: nameOrigin.countryOrigin, isFATFBlacklist: nameOrigin.isFATFBlacklist },
    });

    notify(`Name origin: ${COUNTRY_NAMES[nameOrigin.countryOrigin] || nameOrigin.countryOrigin || "Unknown"}${nameOrigin.isFATFBlacklist ? " — ⚠ FATF Blacklist" : nameOrigin.isFATFHighRisk ? " — FATF Grey List" : ""}`);
    setCheckingNameOriginId(null);
  }

  // ── Bulk scan ───────────────────────────────────────────────────────────────
  async function runBulkScan() {
    setBulkScanning(true);
    const pending = customers.filter(c => c.sanctions === "pending");
    for (const c of pending) {
      await runScreening(c.id);
      await new Promise(r => setTimeout(r, 400)); // rate-limit courtesy delay
    }
    setBulkScanning(false);
    notify(`Bulk scan complete — ${pending.length} customer${pending.length !== 1 ? "s" : ""} screened.`);
  }

  // ── CSV import ──────────────────────────────────────────────────────────────
  function handleCSVFile(file) {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      setCsvText(text);
      setCsvPreview(processCSV(text));
    };
    reader.readAsText(file);
  }

  async function importCSV() {
    const newCustomers = csvPreview;
    if (!newCustomers.length) return;
    const saved = [];
    for (const c of newCustomers) {
      const added = await addCustomer(c);
      saved.push(added);
    }
    notify(`${saved.length} customer${saved.length !== 1 ? "s" : ""} imported.`);
    setShowCSVModal(false);
    setCsvText(""); setCsvPreview([]);
  }

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => customers.filter(c => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || c.name.toLowerCase().includes(q) || c.country?.toLowerCase().includes(q);
    const matchesRisk   = riskFilter === "All" || c.riskLevel === riskFilter;
    const matchesStatus = statusFilter === "All"
      || (statusFilter === "Pending"  && (c.sanctions === "pending"))
      || (statusFilter === "Matches"  && (c.sanctions === "failed" || c.watchlist === "failed" || c.criminal === "failed"))
      || (statusFilter === "Clear"    && c.sanctions === "passed" && c.watchlist === "passed" && c.criminal === "passed")
      || (statusFilter === "Overdue"  && isKYCRefreshOverdue(c));
    return matchesSearch && matchesRisk && matchesStatus;
  }), [customers, searchQuery, riskFilter, statusFilter]);

  const stats = useMemo(() => ({
    total:   customers.length,
    pending: customers.filter(c => c.sanctions === "pending").length,
    matches: customers.filter(c => c.sanctions === "failed" || c.watchlist === "failed" || c.criminal === "failed").length,
    clear:   customers.filter(c => c.sanctions === "passed" && c.watchlist === "passed" && c.criminal === "passed").length,
    overdue: customers.filter(isKYCRefreshOverdue).length,
  }), [customers]);

  // ── Add customer modal state ────────────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [useExistingCustomer, setUseExistingCustomer] = useState(false);
  const [selectedExistingCustomer, setSelectedExistingCustomer] = useState(null);
  const [addForm, setAddForm] = useState({ name: "", type: "Individual", country: "", riskLevel: "Medium", notes: "" });

  // Pre-populate form from existing customer
  const handleExistingCustomerSelect = (customer) => {
    setSelectedExistingCustomer(customer);
    setAddForm({
      name: customer.name || "",
      type: customer.type || "Individual",
      country: customer.country || "",
      riskLevel: customer.riskLevel || "Medium",
      notes: customer.notes || "",
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>

      {/* Global notification toast */}
      {notification && (
        <div style={{ position: "fixed", top: 20, right: 24, zIndex: 9999,
          background: notification.type === "error" ? C.danger : C.success,
          color: "#fff", padding: "12px 20px", borderRadius: 9, boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
          fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600,
          maxWidth: 380, lineHeight: 1.5 }}>
          {notification.message}
        </div>
      )}

      {/* Page Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", color: C.text, fontSize: 26, fontWeight: 800, margin: 0 }}>
            {t('kyc.pageTitle')}
          </h1>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 5 }}>
            {t('kyc.pageSubtitle')}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn variant="ghost" small onClick={() => setShowCSVModal(true)}>⬆ {t('kyc.importCSV')}</Btn>
          <Btn variant="subtle" small disabled={bulkScanning || stats.pending === 0} onClick={runBulkScan}>
            {bulkScanning ? t('kyc.scanning') : `⟳ ${t('kyc.scanAll')} (${stats.pending})`}
          </Btn>
          <Btn onClick={() => { setAddForm({ name: "", type: "Individual", country: "", riskLevel: "Medium", notes: "" }); setShowAddModal(true); }}>
            + {t('kyc.addCustomer')}
          </Btn>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${C.border}` }}>
        {[
          { id: "customers", label: t('kyc.tabs.customers') },
          { id: "settings",  label: t('kyc.tabs.settings') },
        ].map((tb, i) => (
          <button key={tb.id} onClick={() => setActiveTab(tb.id)}
            style={{ padding: "10px 22px", border: "none", background: "transparent",
              color: activeTab === tb.id ? C.accent : C.textDim, cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: activeTab === tb.id ? 700 : 400,
              borderBottom: `2px solid ${activeTab === tb.id ? C.accent : "transparent"}`,
              marginBottom: -1 }}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* ── Settings Tab ─────────────────────────────────────────────────────── */}
      {activeTab === "settings" && <SettingsPanel C={C} />}

      {/* ── Customers Tab ────────────────────────────────────────────────────── */}
      {activeTab === "customers" && (
        <>
          {/* Stats Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: t('kyc.stats.total'),   value: stats.total,   color: C.accent },
              { label: t('kyc.stats.pending'),  value: stats.pending, color: C.warn },
              { label: t('kyc.stats.matches'),  value: stats.matches, color: C.danger },
              { label: t('kyc.stats.clear'),    value: stats.clear,   color: C.success },
              { label: t('kyc.stats.overdue'),  value: stats.overdue, color: stats.overdue > 0 ? C.danger : C.textDim },
            ].map(s => (
              <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 9, padding: "14px 18px", textAlign: "center" }}>
                <div style={{ color: s.color, fontFamily: "'Syne', sans-serif",
                  fontSize: 28, fontWeight: 800 }}>{s.value}</div>
                <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Overdue warning banner */}
          {stats.overdue > 0 && (
            <div style={{ background: "rgba(255,68,68,0.07)", border: `1px solid rgba(255,68,68,0.3)`,
              borderRadius: 9, padding: "12px 18px", marginBottom: 16,
              display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ color: C.danger, fontSize: 18 }}>⚠</span>
              <div>
                <div style={{ color: C.danger, fontWeight: 700, fontSize: 13 }}>
                  {stats.overdue} customer{stats.overdue !== 1 ? "s" : ""} overdue for KYC refresh
                </div>
                <div style={{ color: C.textMid, fontSize: 12 }}>
                  High-risk customers require re-screening every 180 days; Medium/Low every 365 days.
                </div>
              </div>
              <button onClick={() => setStatusFilter("Overdue")}
                style={{ marginLeft: "auto", background: "transparent",
                  border: `1px solid ${C.danger}`, borderRadius: 6, color: C.danger,
                  padding: "5px 14px", cursor: "pointer", fontFamily: "'DM Mono', monospace",
                  fontSize: 11, fontWeight: 600 }}>
                Filter Overdue
              </button>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('kyc.searchPlaceholder')}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
                color: C.text, padding: "8px 12px", fontFamily: "'DM Mono', monospace",
                fontSize: 12, outline: "none", width: 220 }} />
            {[
              { state: riskFilter,   setter: setRiskFilter,   options: ["All", "High", "Medium", "Low"] },
              { state: statusFilter, setter: setStatusFilter, options: ["All", "Pending", "Matches", "Clear", "Overdue"] },
            ].map((f, fi) => (
              <select key={fi} value={f.state} onChange={e => f.setter(e.target.value)}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
                  color: C.text, padding: "8px 12px", fontFamily: "'DM Mono', monospace",
                  fontSize: 12, outline: "none", cursor: "pointer" }}>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            {(riskFilter !== "All" || statusFilter !== "All" || searchQuery) && (
              <Btn variant="ghost" small onClick={() => { setRiskFilter("All"); setStatusFilter("All"); setSearchQuery(""); }}>
                ✕ {t('common.clearFilters')}
              </Btn>
            )}
          </div>

          {/* Customer Table */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            {/* Table header */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.9fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr 0.9fr",
              padding: "10px 16px", background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
              {["Customer", "Type", "Country", "Risk", "Sanctions", "Watchlist", "Criminal", "Last Screened", ""].map((h, i) => (
                <div key={i} style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em", fontWeight: 600 }}>
                  {h.toUpperCase()}
                </div>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: "48px 0", textAlign: "center", color: C.textDim, fontSize: 13 }}>
                {customers.length === 0 ? t('kyc.emptyState') : t('kyc.noResults')}
              </div>
            ) : (
              filtered.map((c, idx) => {
                const overdue = isKYCRefreshOverdue(c);
                return (
                  <div key={c.id}
                    onClick={() => setSelectedCustomer(c)}
                    style={{ display: "grid",
                      gridTemplateColumns: "2fr 0.8fr 0.9fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr 0.9fr",
                      padding: "12px 16px", cursor: "pointer",
                      borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}` : "none",
                      background: selectedCustomer?.id === c.id
                        ? `${C.accent}12`
                        : overdue
                        ? `rgba(255,68,68,0.04)`
                        : idx % 2 === 0 ? "transparent" : `${C.surfaceAlt}55`,
                      transition: "background 0.12s",
                      alignItems: "center" }}>
                    <div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                      {overdue && (
                        <div style={{ color: C.danger, fontSize: 10, marginTop: 2,
                          fontFamily: "'DM Mono', monospace" }}>⚠ KYC refresh overdue</div>
                      )}
                    </div>
                    <div style={{ color: C.textMid, fontSize: 11 }}>{c.type}</div>
                    <div style={{ color: C.textMid, fontSize: 11 }}>{c.country || "—"}</div>
                    <div><RiskBadge level={c.riskLevel} /></div>
                    {["sanctions", "watchlist", "criminal"].map(key => (
                      <div key={key}>
                        <Badge
                          text={scanningId === c.id ? "…" : (c[key] || "pending")}
                          type={scanningId === c.id ? "scanning" : (c[key] === "passed" ? "passed" : c[key] === "failed" ? "failed" : "pending")}
                        />
                      </div>
                    ))}
                    <div style={{ color: C.textDim, fontSize: 11 }}>
                      {c.lastScreened ? new Date(c.lastScreened).toLocaleDateString() : "—"}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <Btn small variant="subtle"
                        disabled={scanningId === c.id}
                        onClick={() => runScreening(c.id)}>
                        {scanningId === c.id ? "…" : t('common.screen')}
                      </Btn>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* ── Add Customer Modal ────────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex",
          alignItems: "center", justifyContent: "center" }} onClick={() => setShowAddModal(false)}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
          <div onClick={e => e.stopPropagation()}
            style={{ position: "relative", background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: 28, width: 460, maxWidth: "95vw", zIndex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", color: C.text,
              fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
              {t('kyc.addCustomer')}
            </div>

            {/* Toggle between manual entry and selecting from existing */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: C.text, fontSize: 13 }}>
                <input type="radio" checked={!useExistingCustomer} onChange={() => { setUseExistingCustomer(false); setSelectedExistingCustomer(null); }} />
                Manually Enter
              </label>
              {customers.length > 0 && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: C.text, fontSize: 13 }}>
                  <input type="radio" checked={useExistingCustomer} onChange={() => setUseExistingCustomer(true)} />
                  Select from Customers ({customers.length})
                </label>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {useExistingCustomer && customers.length > 0 ? (
                // EXISTING CUSTOMER SELECTION MODE
                <div>
                  <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>
                    CUSTOMER
                  </div>
                  <select
                    value={selectedExistingCustomer?.id || ""}
                    onChange={e => {
                      const cust = customers.find(c => c.id === e.target.value);
                      if (cust) handleExistingCustomerSelect(cust);
                    }}
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                      borderRadius: 6, color: C.text, padding: "8px 10px",
                      fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                    <option value="">-- Select a customer --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.type} · {c.country || "—"}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* FORM FIELDS */}
              {[
                { label: t('kyc.fields.fullName'), key: "name", placeholder: "Full legal name" },
                { label: t('kyc.fields.country'),  key: "country", placeholder: "Country of residence/registration" },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>
                    {f.label.toUpperCase()}
                  </div>
                  <input value={addForm[f.key]} onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                      borderRadius: 6, color: C.text, padding: "8px 10px",
                      fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none",
                      boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  { label: t('kyc.fields.type'), key: "type", options: ["Individual", "Business", "Entity"] },
                  { label: t('kyc.fields.riskLevel'), key: "riskLevel", options: ["Low", "Medium", "High"] },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>
                      {f.label.toUpperCase()}
                    </div>
                    <select value={addForm[f.key]} onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                        borderRadius: 6, color: C.text, padding: "8px 10px",
                        fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                      {f.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <Btn variant="ghost" onClick={() => setShowAddModal(false)}>{t('common.cancel')}</Btn>
              <Btn disabled={!addForm.name.trim()} onClick={async () => {
                await addCustomer(addForm);
                setShowAddModal(false);
              }}>{t('kyc.addCustomer')}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Import Modal ──────────────────────────────────────────────────── */}
      {showCSVModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex",
          alignItems: "center", justifyContent: "center" }} onClick={() => setShowCSVModal(false)}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
          <div onClick={e => e.stopPropagation()}
            style={{ position: "relative", background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: 28, width: 560, maxWidth: "95vw", zIndex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", color: C.text,
              fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              {t('kyc.importCSV')}
            </div>
            <div style={{ color: C.textDim, fontSize: 12, marginBottom: 18 }}>
              CSV must have columns: <code style={{ fontFamily: "'DM Mono', monospace",
                fontSize: 11 }}>name, type, country, risk_level, notes</code>
            </div>
            <input ref={fileInputRef} type="file" accept=".csv"
              style={{ display: "none" }}
              onChange={e => { if (e.target.files[0]) handleCSVFile(e.target.files[0]); }} />
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <Btn variant="subtle" onClick={() => fileInputRef.current?.click()}>
                📁 Choose File
              </Btn>
              <Btn variant="ghost" onClick={() => {
                const csv = "name,type,country,risk_level,notes\nJohn Doe,Individual,US,Medium,Test customer\nAcme Corp,Business,GB,Low,UK entity";
                const a = document.createElement("a");
                a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
                a.download = "kyc_template.csv"; a.click();
              }}>⬇ Template</Btn>
            </div>
            {csvPreview.length > 0 && (
              <div style={{ background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 7, padding: 12, marginBottom: 16,
                maxHeight: 180, overflowY: "auto" }}>
                <div style={{ color: C.success, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                  ✓ {csvPreview.length} customer{csvPreview.length !== 1 ? "s" : ""} ready to import
                </div>
                {csvPreview.slice(0, 5).map((c, i) => (
                  <div key={i} style={{ color: C.textMid, fontSize: 11, padding: "3px 0",
                    borderBottom: `1px solid ${C.border}` }}>
                    {c.name} · {c.type} · {c.country || "—"} · <RiskBadge level={c.riskLevel} />
                  </div>
                ))}
                {csvPreview.length > 5 && (
                  <div style={{ color: C.textDim, fontSize: 11, marginTop: 4 }}>
                    …and {csvPreview.length - 5} more
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn variant="ghost" onClick={() => { setShowCSVModal(false); setCsvText(""); setCsvPreview([]); }}>
                {t('common.cancel')}
              </Btn>
              <Btn disabled={csvPreview.length === 0} onClick={importCSV}>
                Import {csvPreview.length > 0 ? csvPreview.length : ""} Customers
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Detail Drawer ─────────────────────────────────────────────── */}
      {selectedCustomer && (
        <CustomerDrawer
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onSave={saveCustomer}
          onRunScreening={runScreening}
          onRunNameOrigin={runNameOrigin}
          scanningId={scanningId}
          checkingNameOriginId={checkingNameOriginId}
        />
      )}
    </div>
  );
}